import { BoxItem, AddPackBoxItem, PackSourceItem, PackSourceItemLot, PackBox, PackOrder } from "@/types/packing";
import { Form, InputNumber, Input, Modal, Typography, DescriptionsProps, notification, Flex, Button } from "antd";
import { FC, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import usePackOrderDetail from "../hooks";
import { PackOrderContext } from "../context";
import ProductDesc from "@/components/Desc/ProductDesc";
import { numFormmater } from "@/pages/Requirement/util";
import { CountrySelect, SelectBrandName, SelectBrandModel, SelectProductType } from "@/components/Selector";
import SText from "@/styles/SText";
import SelectSourceItem from "./SelectSourceItem";
import SelectSourceItemLot, { DEFAULT_EXT_KEY } from "./SelectSourceItemLot";
import SelectCurrencyVal from "@/components/Selector/SelectCurrencyVal";
import { generateSequence, allocateCurrentBoxItems, aggregateBoxItems, allocateCopyBoxItems, applyLotPrice } from "./utils";
import ExportProgressModal from "@/components/ExportProgressModal";
import { DictContext } from "@/layout/context/DictContext";
interface BaseProps {
  boxId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: (newItem: BoxItem) => void;
}
interface AddProps {
  formType: "ADD";
  item: null;
};
interface EditProps {
  formType: "EDIT";
  item: BoxItem;
};
type ItemAddOrEditProps = (AddProps | EditProps) & BaseProps;

const ItemAddOrEdit: FC<ItemAddOrEditProps> = ({ formType, open, boxId, item, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { addBox, addItem, editItem, loading } = usePackOrderDetail();
  const { sourceItems, mainCurrency, orderData, setOrderData, purLotMap } = useContext(PackOrderContext);
  const [form] = Form.useForm<AddPackBoxItem>();
  const { currencys } = useContext(DictContext);

  const [product, setProduct] = useState<PackSourceItem | null>(null);
  const [selLot, setSelLot] = useState<PackSourceItemLot | null>(null);
  const [lotList, setLotList] = useState<PackSourceItemLot[]>([]);
  const [priceLoading, setPriceLoading] = useState<boolean>(false);
  const [openCopy, setOpenCopy] = useState<boolean>(false);
  const [copying, setCopying] = useState<boolean>(false);
  const [copyLength, setCopyLength] = useState<number>(1);
  const [copyProgress, setCopyProgress] = useState<number>(0);
  const [proType, seProType] = useState<string | undefined>(undefined);
  // 监听品牌字段，驱动品牌型号的禁用状态与数据拉取
  const brandVal = Form.useWatch("brand", form);

  useEffect(() => {
    if (open && formType === "EDIT") {
      form.setFieldsValue({
        productId: item.productId,
        lot: item.lot ?? '',
        count: item.count,
        declaredUnitPrice: item.declaredUnitPrice,
        declaredCurrency: item.declaredCurrency,
        hsCode: item.hsCode,
        originPlace: item.originPlace,
        productNameCn: item.productNameCn,
        productNameEn: item.productNameEn,
        remark: item.remark,
        brand: item.brand,
        brandType: item.brandType,
        exportBrandName: item.exportBrandName,
        netWeight: item.netWeight
      });
      let p = sourceItems.find(s => s.productId === item.productId);
      setProduct(p || null);
      setLotList([...(p?.lots || [])]);
    } else if (open && formType === "ADD") {
      form.resetFields();
      form.setFieldValue("declaredCurrency", mainCurrency);
    }
  }, [open, formType, item, sourceItems, form]);


  const { newBoxNumbers, newBoxStr } = useMemo(() => {
    let newBoxNumbers: string[] = [];
    let newBoxStr: string = '';
    if (copyLength > 1 && formType === "ADD" && orderData?.boxes) {
      let endBox = orderData.boxes[orderData.boxes.length - 1];
      newBoxNumbers = generateSequence(endBox.boxNumber, copyLength - 1);
      if (newBoxNumbers.length === 1) newBoxStr = newBoxNumbers[0]
      else newBoxStr = `${newBoxNumbers[0]}-${newBoxNumbers[copyLength - 2]}`
    }
    return { newBoxNumbers, newBoxStr };
  }, [formType, orderData, copyLength]);

  const handleSubmit = async () => {
    setOpenCopy(false);
    try {
      let values: AddPackBoxItem = await form.validateFields();
      if (!!!values.lot && values.lot !== '') return notification.error({ message: t('warehouse.selectLot') })
      if (formType === "ADD" && product && selLot) {
        // 当前箱：分配明细（跨批次凑数）并装完，更新 newOrderBox 与 sourceItems
        let newOrderBox: PackBox[] = [...(orderData.boxes || [])];
        let getCurrencyDesc = (key?: string) => currencys.find(cu => cu.key === key)?.desc;
        let { items: curItems, newSourceItems } = allocateCurrentBoxItems(sourceItems, values.productId, selLot, values);
        applyLotPrice(curItems, purLotMap, getCurrencyDesc);
        let curNewItems: BoxItem[] = [];
        for (let i = 0; i < curItems.length; i++) {
          let curBoxItem = await addItem(boxId, curItems[i], true);
          !!curBoxItem && curNewItems.push(curBoxItem);
        };
        let idx = newOrderBox.findIndex(b => b.id === boxId);
        if (idx > -1) {
          if (newOrderBox[idx].items) newOrderBox[idx].items.push(...curNewItems)
          else newOrderBox[idx].items = [...curNewItems];
        };
        // 多箱复制：以最新 box 的明细聚合（productId+brand）为目标，从剩余批次依次分配
        if (copyLength > 1) {
          let box = newOrderBox.find(b => b.id === boxId);
          if (box && box.items?.length) {
            setCopying(true);
            setCopyProgress(1);
            let groups = aggregateBoxItems(box.items);
            let { groupsPerBox } = await allocateCopyBoxItems(newSourceItems, groups, newBoxNumbers.length);
            groupsPerBox.forEach(boxItems => applyLotPrice(boxItems, purLotMap, getCurrencyDesc));

            let resBox: PackBox[] = [];
            for (let i = 0; i < newBoxNumbers.length; i++) {
              let newBox = await addBox({
                boxNumber: newBoxNumbers[i] || undefined,
                lengthMm: box.lengthMm,
                widthMm: box.widthMm,
                heightMm: box.heightMm,
                packageType: box.packageType,
                marksNumbers: box.marksNumbers,
                remark: box.remark,
              }, orderData.id, true);
              let itemsData = groupsPerBox[i] || [];
              if (newBox) {
                for (let j = 0; j < itemsData.length; j++) {
                  let item = await addItem(newBox.id, itemsData[j], true);
                  if (item) {
                    if (newBox.items) newBox.items.push({ ...item })
                    else newBox.items = [{ ...item }]
                  }
                };
                resBox.push(newBox);
              }
              setCopyProgress(last => last + 1);
            };
            if (resBox.length > 0) newOrderBox.push(...resBox);
          }
        };
        setOrderData({
          ...orderData,
          boxes: [...newOrderBox]
        });
        setTimeout(() => {
          setCopying(false);
          notification.success({ message: t('common.addSuccess') });
          onSuccess(curNewItems[0]);
        }, 500)
      }
      if (formType === "EDIT") {
        let changed: boolean = false;
        (Object.keys(values) as (keyof AddPackBoxItem)[]).forEach((key) => {
          if (values[key] !== item[key]) changed = true;
        })
        if (changed) {
          const newItem = await editItem(item.id, values);
          !!newItem && onSuccess(newItem);
        } else onClose();
      }
    } catch (error) {
      console.log(">>>>", error);

    }
  };

  const closeCopy = () => {
    setOpenCopy(false);
    setCopyLength(1);
  }

  const handleProduct = async (info: PackSourceItem) => {
    setProduct(info);
    setLotList([...(info.lots || [])])
    form.setFieldsValue({
      productId: info.product.id,
      hsCode: info.product.hsCode ?? undefined,
      count: info.remainingCount
    });
    let lot = info.lots.find(l => l.remainingCount > 0);
    !!lot && handleLot(lot, info);
  };

  const handleLot = (info: PackSourceItemLot, productInfo?: PackSourceItem) => {
    let key = `${productInfo?.productId ?? product?.productId ?? ''}<->${info.lot}`;
    let purItem = purLotMap[key];
    setSelLot(info);
    form.setFieldsValue({
      lot: info.lot,
      brand: info.exts?.originalBrand,
      originPlace: info.exts?.originPlace,
      declaredCurrency: currencys.find(cu => cu.key === purItem?.order?.currency)?.desc,
      declaredUnitPrice: Number(purItem?.priceStr ?? "0")
    });
  };

  const ProductInfoItems: DescriptionsProps["items"] = [
    {
      key: "product",
      label: t("product.code"),
      children: product?.product?.code || "",
    },
    {
      key: "name",
      label: t("product.name"),
      children: product?.product?.name || "",
    },
    {
      key: "uom",
      label: t("product.uom"),
      children: product?.product?.uom?.toLocaleUpperCase() || "",
    },
    {
      key: "model",
      label: t("order.brand"),
      children: <SText style={{ flex: 1 }} text={product?.product?.model || ""} />,
      span: 3,
    },
    {
      key: "count",
      label: t("warehouse.boxItemQty"),
      children: numFormmater(product?.demandCount ?? 0),
    },
    {
      key: "boxCount",
      label: t("warehouse.boxedCount"),
      children: numFormmater(product?.packedCount ?? 0),
    },
    {
      key: "remainPack",
      label: t("warehouse.remainPack"),
      children: numFormmater((product?.remainingCount ?? 0) + (item?.count ?? 0)),
    },
    // {
    //   key: "brand",
    //   label: t("warehouse.brand"),
    //   children: product?.ext?.brand ?? '',
    // },
  ]

  return (
    <Modal width={600} maskClosable={false} centered
      title={formType === "ADD" ? t('warehouse.boxAddItem') : t('warehouse.boxEditItem')}
      open={open}
      onCancel={onClose}
      okButtonProps={{ disabled: loading === "ItemAdd" || loading === "ItemEdit" }}
      destroyOnClose
      footer={
        <Flex justify="flex-end" gap={6}>
          {formType === "ADD" && <Button type="dashed" onClick={() => setOpenCopy(true)}>{t('warehouse.multiBox')}</Button>}
          <Button type="primary" onClick={handleSubmit}>{t('common.confirm')}</Button>
          <Button type="default" onClick={onClose}>{t('common.cancel')}</Button>
        </Flex>
      }
    >
      <Form form={form}>
        <Form.Item hidden={formType === "EDIT"} label={t('warehouse.selectProduct')} style={{ marginBottom: !!product ? 0 : 24 }}>
          <SelectSourceItem loading={priceLoading} list={sourceItems} onChangeItem={handleProduct} placeholder={t('warehouse.selectProduct')} />
        </Form.Item>
        {
          !!product && <Form.Item noStyle>
            <Typography.Title level={5} style={{ margin: "0 0 -2px" }}>{t('purchase.productInfo')}</Typography.Title>
            <ProductDesc product={product.product} showtype="OTHER" otherItems={ProductInfoItems} />
          </Form.Item>
        }
        <Form.Item<AddPackBoxItem> name="productId" hidden />
        <Form.Item<AddPackBoxItem> name="lot" label={t('warehouse.selectLot')}
        // rules={[{ required: true, message: t('warehouse.selectLot') }]}
        >
          <SelectSourceItemLot list={lotList || []} onChangeLot={handleLot}
            extOptionData={{ [item?.lot ?? DEFAULT_EXT_KEY]: item?.count ?? 0 }}
            placeholder={t('warehouse.selectLot')}
          />
        </Form.Item>
        <Form.Item
          label={t('warehouse.productType')}
        >
          <SelectProductType value={proType} onChange={seProType} />
        </Form.Item>
        <Form.Item<AddPackBoxItem>
          name="brand"
          label={t('warehouse.brand')}
        >
          <SelectBrandName disabled={!!!proType} productType={proType || ''} />
        </Form.Item>
        <Form.Item<AddPackBoxItem>
          name="brandType"
          label={t('purchase.brandModel')}
        >
          <SelectBrandModel disabled={!!!brandVal || !!!proType} productType={proType || ''} brandName={brandVal || ''} />
        </Form.Item>
        <Form.Item<AddPackBoxItem>
          name="exportBrandName"
          label={t('warehouse.exportBrandName')}
        >
          <Input placeholder={t('warehouse.exportBrandName')} />
        </Form.Item>
        <Form.Item<AddPackBoxItem> style={{ marginTop: !!product ? 6 : 0 }}
          name="count"
          label={t('warehouse.quantity')}
          rules={[{ required: true, message: t('warehouse.quantity') }]}
        >
          <InputNumber changeOnWheel style={{ width: '100%' }} min={0}
            placeholder={t('warehouse.quantity')}
            max={product ? product.remainingCount + (item?.count ?? 0) : undefined}
          />
        </Form.Item>
        <Form.Item<AddPackBoxItem>
          name="netWeight"
          label={t('warehouse.netWeightG')}
          rules={[{ required: formType === "ADD", message: t('warehouse.netWeightG') }]}
        >
          <InputNumber changeOnWheel style={{ width: '100%' }}
            placeholder={t('warehouse.netWeightG')} min={0}
          />
        </Form.Item>
        <Form.Item<AddPackBoxItem>
          name="declaredUnitPrice"
          label={t('warehouse.declaredUnitPrice')}
          rules={[{ required: true, message: t('warehouse.declaredUnitPrice') }]}
        >
          <InputNumber changeOnWheel style={{ width: '100%' }} min={0} placeholder={t('warehouse.declaredUnitPrice')} />
        </Form.Item>
        <Form.Item<AddPackBoxItem>
          name="declaredCurrency"
          label={t('warehouse.declaredCurrency')}
          rules={[{ required: true, message: t('warehouse.declaredCurrency') }]}
        >
          <SelectCurrencyVal />
        </Form.Item>
        <Form.Item<AddPackBoxItem>
          name="hsCode"
          label={t('warehouse.hsCode')}
        >
          <Input placeholder={t('warehouse.hsCode')} />
        </Form.Item>
        <Form.Item<AddPackBoxItem>
          name="originPlace"
          label={t('warehouse.originPlace')}
        >
          <CountrySelect />
        </Form.Item>
        <Form.Item<AddPackBoxItem>
          name="productNameCn"
          label={t('warehouse.productNameCn')}
        >
          <Input placeholder={t('warehouse.productNameCn')} />
        </Form.Item>
        <Form.Item<AddPackBoxItem>
          name="productNameEn"
          label={t('warehouse.productNameEn')}
        >
          <Input placeholder={t('warehouse.productNameEn')} />
        </Form.Item>
        <Form.Item<AddPackBoxItem>
          name="remark"
          label={t('warehouse.remark')}
          layout="vertical"
          style={{ marginBottom: 80 }}
        >
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} maxLength={500} showCount placeholder={t('warehouse.remark')} />
        </Form.Item>
      </Form>
      <Modal open={openCopy} title={t('warehouse.multiBox')} width={600} onCancel={closeCopy} onOk={handleSubmit}>
        <div>{t('warehouse.addingTip')}</div>
        <Form.Item label={t('warehouse.addPackQty')} layout="horizontal" >
          <InputNumber changeOnWheel style={{ width: "100%" }} min={1} placeholder={t('warehouse.addPackQty')} value={copyLength} onChange={val => setCopyLength(val || 1)} />
        </Form.Item>
        {copyLength > 1 && <div style={{ textAlign: "right" }}>{t('warehouse.newBoxNumber')}: {newBoxStr}</div>}
      </Modal>
      <ExportProgressModal progress={Math.round(copyProgress / copyLength * 100)} visible={copying} title={t('warehouse.multiBox') + "..."} processLabel={t('warehouse.multiBox')} />
    </Modal>
  )
};

export default ItemAddOrEdit;